// frontend/src/modules/relatorios/pages/RelatoriosAdmin.js
import React, { useState, useEffect } from 'react';
import { FaFilePdf, FaFileExcel, FaBuilding, FaUsers, FaMoneyBillWave, FaChartLine } from 'react-icons/fa';
import { exportToPDF, exportToExcel } from '../../../services/exportService';
import { useMediaQuery } from 'react-responsive';

const API_URL = process.env.REACT_APP_API_URL || '';

function RelatoriosAdmin() {
  const [dados, setDados] = useState({
    totalEmpresas: 0,
    totalUsuarios: 0,
    totalReceitas: 0,
    totalDespesas: 0,
    saldo: 0,
    empresas: [],
    receitasPorEmpresa: {},
    despesasPorEmpresa: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periodo, setPeriodo] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ maxWidth: 992 });
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDados();
  }, [periodo]);

  const fetchDados = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/relatorios/consolidado?mes=${periodo.mes}&ano=${periodo.ano}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Dados consolidados:', data);

      const receitasPorEmpresa = {};
      const despesasPorEmpresa = {};

      if (data.empresas && data.empresas.length > 0) {
        data.empresas.forEach(emp => {
          const nome = emp.nome || `Empresa ${emp.id}`;
          receitasPorEmpresa[nome] = emp.receitas || 0;
          despesasPorEmpresa[nome] = emp.despesas || 0;
        });
      }

      setDados({
        totalEmpresas: data.total_empresas || 0,
        totalUsuarios: data.total_usuarios || 0,
        totalReceitas: data.total_receitas || 0,
        totalDespesas: data.total_despesas || 0,
        saldo: data.saldo_consolidado || 0,
        empresas: data.empresas || [],
        receitasPorEmpresa,
        despesasPorEmpresa
      });
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(value);
  };

  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const prepararDadosExportacao = () => {
    const dadosExport = dados.empresas.map(emp => ({
      Empresa: emp.nome || `Empresa ${emp.id}`,
      CNPJ: emp.cnpj || '-',
      Receitas: emp.receitas || 0,
      Despesas: emp.despesas || 0,
      Saldo: (emp.receitas || 0) - (emp.despesas || 0),
      'Total Lançamentos': emp.total_lancamentos || 0
    }));
    return dadosExport;
  };

  const handleExportPDF = () => {
    const dadosExport = prepararDadosExportacao();
    if (dadosExport.length === 0) {
      alert('Nenhum dado para exportar');
      return;
    }
    exportToPDF(dadosExport, 'Relatorio_Consolidado_Admin');
  };

  const handleExportExcel = () => {
    const dadosExport = prepararDadosExportacao();
    if (dadosExport.length === 0) {
      alert('Nenhum dado para exportar');
      return;
    }
    exportToExcel(dadosExport, 'Relatorio_Consolidado_Admin');
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando dados consolidados...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>❌</div>
        <p style={{ color: 'red', fontSize: isMobile ? '14px' : '16px' }}>Erro: {error}</p>
        <button 
          onClick={fetchDados}
          style={{ 
            marginTop: '10px', 
            padding: isMobile ? '8px 20px' : '10px 20px', 
            backgroundColor: '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            fontSize: isMobile ? '13px' : '14px',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>📄 Relatórios Consolidados</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Visão geral financeira de todas as empresas
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <select 
            value={periodo.mes} 
            onChange={(e) => setPeriodo({ ...periodo, mes: parseInt(e.target.value) })} 
            style={{ 
              padding: isMobile ? '6px 10px' : '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0'
            }}
          >
            {meses.map((m, i) => (<option key={i + 1} value={i + 1}>{m}</option>))}
          </select>
          <select 
            value={periodo.ano} 
            onChange={(e) => setPeriodo({ ...periodo, ano: parseInt(e.target.value) })} 
            style={{ 
              padding: isMobile ? '6px 10px' : '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0'
            }}
          >
            {[2024, 2025, 2026, 2027].map(a => (<option key={a} value={a}>{a}</option>))}
          </select>
          <button 
            onClick={handleExportPDF}
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            <FaFilePdf /> PDF
          </button>
          <button 
            onClick={handleExportExcel}
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            <FaFileExcel /> Excel
          </button>
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(4, 1fr)', 
        gap: isMobile ? '10px' : '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ backgroundColor: '#cce5ff', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: 0, color: '#004085', fontSize: isMobile ? '14px' : '18px' }}><FaBuilding /> Empresas</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#004085' }}>
            {dados.totalEmpresas}
          </p>
        </div>
        <div style={{ backgroundColor: '#d4edda', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: 0, color: '#155724', fontSize: isMobile ? '14px' : '18px' }}><FaUsers /> Usuários</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#155724' }}>
            {dados.totalUsuarios}
          </p>
        </div>
        <div style={{ 
          backgroundColor: '#d4edda', 
          padding: isMobile ? '15px' : '20px', 
          borderRadius: '8px', 
          textAlign: 'center', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          gridColumn: isMobile ? '1' : isTablet ? 'auto' : 'auto'
        }}>
          <h3 style={{ margin: 0, color: '#155724', fontSize: isMobile ? '14px' : '18px' }}><FaMoneyBillWave /> Receitas</h3>
          <p style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#155724' }}>
            {formatCurrency(dados.totalReceitas)}
          </p>
        </div>
        <div style={{ 
          backgroundColor: '#f8d7da', 
          padding: isMobile ? '15px' : '20px', 
          borderRadius: '8px', 
          textAlign: 'center', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          gridColumn: isMobile ? '1' : isTablet ? 'auto' : 'auto'
        }}>
          <h3 style={{ margin: 0, color: '#721c24', fontSize: isMobile ? '14px' : '18px' }}><FaChartLine /> Despesas</h3>
          <p style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#721c24' }}>
            {formatCurrency(dados.totalDespesas)}
          </p>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: isMobile ? '15px' : '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📊 DRE Consolidado</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', fontSize: isMobile ? '13px' : '14px' }}>Receita Total</td>
                <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', color: '#28a745', fontWeight: 'bold', fontSize: isMobile ? '13px' : '14px' }}>
                  {formatCurrency(dados.totalReceitas)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', fontSize: isMobile ? '13px' : '14px' }}>(-) Despesas Totais</td>
                <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', color: '#dc3545', fontWeight: 'bold', fontSize: isMobile ? '13px' : '14px' }}>
                  {formatCurrency(dados.totalDespesas)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: isMobile ? '6px 8px' : '8px', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', borderTop: '2px solid #dee2e6' }}>= Resultado Consolidado</td>
                <td style={{ padding: isMobile ? '6px 8px' : '8px', textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', color: dados.saldo >= 0 ? '#28a745' : '#dc3545', borderTop: '2px solid #dee2e6' }}>
                  {formatCurrency(dados.saldo)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: isMobile ? '15px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📋 Detalhamento por Empresa</h3>
        <div style={{ overflowX: 'auto' }}>
          {dados.empresas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
              <p style={{ color: '#6c757d', fontSize: isMobile ? '13px' : '14px' }}>Nenhuma empresa encontrada</p>
            </div>
          ) : isMobile ? (
            <div>
              {dados.empresas.map((empresa, index) => {
                const receitas = empresa.receitas || 0;
                const despesas = empresa.despesas || 0;
                const saldo = receitas - despesas;
                return (
                  <div key={index} style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '10px', borderLeft: `4px solid ${saldo >= 0 ? '#28a745' : '#dc3545'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                      <strong style={{ fontSize: '14px' }}>{empresa.nome || `Empresa ${empresa.id}`}</strong>
                      <span style={{ fontWeight: 'bold', fontSize: '14px', color: saldo >= 0 ? '#28a745' : '#dc3545' }}>{formatCurrency(saldo)}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      <div><strong>CNPJ:</strong> {empresa.cnpj || '-'}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                        <span style={{ color: '#28a745' }}>Receitas: {formatCurrency(receitas)}</span>
                        <span style={{ color: '#dc3545' }}>Despesas: {formatCurrency(despesas)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Empresa</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>CNPJ</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Receitas</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Despesas</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {dados.empresas.map((empresa, index) => {
                  const receitas = empresa.receitas || 0;
                  const despesas = empresa.despesas || 0;
                  const saldo = receitas - despesas;
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '10px' }}><strong>{empresa.nome || `Empresa ${empresa.id}`}</strong></td>
                      <td style={{ padding: '10px' }}>{empresa.cnpj || '-'}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#28a745' }}>{formatCurrency(receitas)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#dc3545' }}>{formatCurrency(despesas)}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: saldo >= 0 ? '#28a745' : '#dc3545' }}>{formatCurrency(saldo)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        
        {dados.empresas.length > 0 && (
          <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '2px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', fontWeight: 'bold', fontSize: isMobile ? '14px' : '16px' }}>
            <span style={{ marginRight: '30px' }}>Total Geral: {formatCurrency(dados.saldo)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default RelatoriosAdmin;